import React, { useState } from 'react';
import { FiX, FiPlay, FiRotateCcw, FiMaximize2, FiCopy, FiDownload } from 'react-icons/fi';
import { executeCode, executeCodeFree } from '../../services/codeExecutionService';

const FullScreenEditor = ({ isOpen, onClose, initialCode, title, language = 'csharp' }) => {
  const [code, setCode] = useState(initialCode);
  const [output, setOutput] = useState('Click "Run Code" to see output...');
  const [isRunning, setIsRunning] = useState(false);
  const [executionTime, setExecutionTime] = useState(null);

  if (!isOpen) return null;

  const handleRun = async () => {
    setIsRunning(true);
    setOutput('🔄 Compiling and running your C# code...\n\nPlease wait...');

    try {
      console.log('Executing code...', code.substring(0, 100));
      
      const result = await executeCode(code, '', language);
      
      console.log('Execution result:', result);

      if (result.success) {
        const outputText = result.output.trim() || '(No output)';
        setOutput(
          `✅ Code executed successfully!\n\n` +
          `📤 Output:\n${outputText}\n\n` +
          `⏱️ Execution Time: ${result.time || 'N/A'}s\n` +
          `💾 Memory Used: ${result.memory || 'N/A'} KB`
        );
        setExecutionTime(result.time);
      } else {
        setOutput(
          `❌ Execution Failed!\n\n` +
          `${result.error}\n\n` +
          `💡 Tips:\n` +
          `• Check your code syntax\n` +
          `• Ensure you have 'using System;'\n` +
          `• Verify Main() method exists\n` +
          `• Look for typos or missing semicolons`
        );
        setExecutionTime(null);
      }
    } catch (error) {
      console.error('Execution error:', error);
      setOutput(
        `❌ Unexpected Error!\n\n` +
        `${error.message}\n\n` +
        `🔧 Troubleshooting:\n` +
        `• Check your internet connection\n` +
        `• Try refreshing the page\n` +
        `• Verify the code is valid C#\n\n` +
        `📝 For better performance, get a free API key:\n` +
        `1. Visit: https://rapidapi.com/judge0-official/api/judge0-ce\n` +
        `2. Sign up (free!)\n` +
        `3. Add key to .env: REACT_APP_RAPIDAPI_KEY=your_key\n` +
        `4. Restart app`
      );
      setExecutionTime(null);
    } finally {
      setIsRunning(false);
    }
  };

  const handleReset = () => {
    setCode(initialCode);
    setOutput('Click "Run Code" to see output...');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    alert('Code copied to clipboard! 📋');
  };

  const handleDownload = () => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}.cs`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fullscreen-editor-overlay">
      <div className="fullscreen-editor">
        {/* Header */}
        <div className="fullscreen-editor-header">
          <div className="fullscreen-editor-title">
            <FiMaximize2 />
            <h2>{title || 'Code Editor'}</h2>
            <span className="language-badge">{language.toUpperCase()}</span>
          </div>
          <div className="fullscreen-editor-actions">
            <button className="editor-btn" onClick={handleCopy} title="Copy Code">
              <FiCopy /> Copy
            </button>
            <button className="editor-btn" onClick={handleDownload} title="Download">
              <FiDownload /> Download
            </button>
            <button className="editor-btn" onClick={handleReset} title="Reset">
              <FiRotateCcw /> Reset
            </button>
            <button 
              className="editor-btn primary" 
              onClick={handleRun} 
              disabled={isRunning}
            >
              <FiPlay /> {isRunning ? 'Running...' : 'Run Code'}
            </button>
            <button className="editor-btn close" onClick={onClose} title="Close">
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Editor Area */}
        <div className="fullscreen-editor-content">
          {/* Code Editor */}
          <div className="fullscreen-editor-pane">
            <div className="editor-pane-header">
              <span>📝 Editor</span>
              <span className="line-count">{code.split('\n').length} lines</span>
            </div>
            <textarea
              className="fullscreen-code-textarea"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              spellCheck={false}
              autoComplete="off"
            />
          </div>

          {/* Output Panel */}
          <div className="fullscreen-output-pane">
            <div className="output-pane-header">
              <span>💻 Output</span>
              {isRunning && <span className="running-indicator">Running...</span>}
            </div>
            <div className="fullscreen-output-content">
              <pre>{output}</pre>
            </div>
          </div>
        </div>

        {/* Footer Tips */}
        <div className="fullscreen-editor-footer">
          <div className="editor-tips">
            💡 <strong>Tips:</strong> 
            Press <kbd>Ctrl</kbd> + <kbd>Enter</kbd> to run code • 
            <kbd>Ctrl</kbd> + <kbd>Z</kbd> to undo • 
            Edit and experiment freely! 🚀
            {executionTime && <span style={{ marginLeft: '16px' }}>⚡ Last run: {executionTime}s</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullScreenEditor;

